import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { adminApi } from '@/lib/adminApi';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Loader2, Pencil, Copy, Briefcase, Minus, Info, Globe, Building2, Search, Files, ArrowUpRight, Eye, Archive, RotateCcw, Trash2, FileSpreadsheet } from 'lucide-react';
import { RoleProfileImportDialog } from '@/components/admin/RoleProfileImportDialog';
import { ModuleBanner } from '@/components/admin/ModuleBanner';
import { useApp } from '@/context/AppContext';
import { useSort } from '@/lib/useSort';
import { SortableHeader } from '@/components/shared/SortableHeader';
import { PendingJobProfileRequests } from '@/components/admin/PendingJobProfileRequests';
import { useDataLabel } from '@/lib/dataI18n';

const slug = (s) => s.toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/\s+/g, '_').slice(0, 50);

const EMPTY_REQ = () => ({ competency_code: '', weight: 1.0, expected_level: 70, minimum_threshold: 50, criticality: 'important' });

const JOB_DEF_FIELDS = [
  ['position_purpose', 'purpose'],
  ['main_responsibilities', 'responsibilities'],
  ['minimum_requirements', 'minRequirements'],
  ['academic_background', 'academic'],
  ['required_experience', 'experience'],
];

export default function AdminRoleProfiles() {
  const { t, i18n } = useTranslation();
  const tData = useDataLabel();
  const { user } = useApp();
  const isHmAdmin = user?.role === 'hm_admin';
  // Catalog cleanup sprint I — localized industry labels (canonical codes preserved)
  const [viewP, setViewP] = useState(null);
  const [rpDelArmed, setRpDelArmed] = useState(false);
  const [items, setItems] = useState([]);
  const [comps, setComps] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [industryFilter, setIndustryFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [forkOpen, setForkOpen] = useState(false);
  const [forkSrc, setForkSrc] = useState(null);
  const [forkCompanyId, setForkCompanyId] = useState('');
  const [companies, setCompanies] = useState([]);

  // Phase 3 — same-tenant duplication + governed promotion to global template.
  const [dupOpen, setDupOpen] = useState(false);
  const [dupSrc, setDupSrc] = useState(null);
  const [dupName, setDupName] = useState('');
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [promoteSrc, setPromoteSrc] = useState(null);
  const [promoteForm, setPromoteForm] = useState(null);
  const [promoteSaving, setPromoteSaving] = useState(false);

  // Prompt 17: industry selector — must pick one BEFORE creating a role profile.
  const [industryPickerOpen, setIndustryPickerOpen] = useState(false);
  const [draftIndustry, setDraftIndustry] = useState('general');
  // Phase 2B — governed Excel mass import (hm_admin only)
  const [importOpen, setImportOpen] = useState(false);

  const reload = () => {
    setLoading(true);
    Promise.all([adminApi.listRoleProfiles(showArchived), adminApi.listCompetencies(false), adminApi.listRoleIndustries(), adminApi.listCompanies({ book: 'all' })])
      .then(([rps, cs, inds, cos]) => { setItems(rps); setComps(cs); setIndustries(inds); setCompanies(cos || []); })
      .catch((e) => toast.error(t('toasts.failedToLoad'), { description: formatApiError(e) }))
      .finally(() => setLoading(false));
  };
  useEffect(reload, [showArchived]);

  const compByCode = useMemo(() => Object.fromEntries(comps.map((c) => [c.code, c])), [comps]);

  // Sort
  const sort = useSort();
  // FIX 4 — deterministic, accent-insensitive alphabetical sorting over the
  // COMPLETE filtered dataset (before rendering).
  const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  // Catalog cleanup sprint I — display industries in the UI language (codes untouched)
  const isEsUi = (i18n.language || 'es').startsWith('es');
  const industryLabel = (code) => {
    const ind = industries.find((x) => x.code === code);
    if (!ind) return code || '';
    return (isEsUi ? ind.label_es : ind.label_en) || ind.label_en || code;
  };
  const filteredItems = useMemo(
    () => {
      const q = norm(search.trim());
      return items.filter((p) => {
        if (industryFilter !== 'all' && p.industry !== industryFilter) return false;
        if (!q) return true;
        const companyName = p.company_id ? (companies.find((c) => c.id === p.company_id)?.name || '') : 'global';
        return [p.position_title, p.code, p.department, p.industry, companyName]
          .some((f) => norm(f).includes(q));
      });
    },
    [items, industryFilter, search, companies]
  );
  const sortedItems = useMemo(() => sort.apply(filteredItems, {
    position:     (p) => norm(tData(p.position_title) || p.position_title),
    code:         (p) => norm(p.code),
    scope:        (p) => (p.company_id ? 'company' : 'global'),
    requirements: (p) => (p.requirements || []).length,
    status:       (p) => (p.archived ? 1 : 0),
  }), [filteredItems, sort]);

  const openNew = () => {
    // Prompt 17: open industry picker first; the actual form opens after a pick
    setEditing(null);
    setDraftIndustry('general');
    setIndustryPickerOpen(true);
  };

  const proceedToNewRole = (industry) => {
    setIndustryPickerOpen(false);
    setForm({
      code: '', position_title: '', department: '', company_id: null, _scope: 'global', _codeManual: false,
      industry,
      suite_codes: ['talentmap'], description: '', role_context_notes: '',
      position_purpose: '', main_responsibilities: '', minimum_requirements: '',
      academic_background: '', required_experience: '',
      requirements: [EMPTY_REQ()], parent_profile_id: null, archived: false,
    });
    setOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...p, _scope: p.company_id ? 'company' : 'global', requirements: (p.requirements || []).map((r) => ({ ...r })) });
    setOpen(true);
  };

  // UAT polish — deterministic auto code: slug of title + collision suffix (client preview)
  const computeAutoCode = (title) => {
    const base = slug(title || '');
    if (!base) return '';
    if (!items.some((p) => p.code === base)) return base;
    let n = 2;
    while (items.some((p) => p.code === `${base}_${n}`)) n += 1;
    return `${base}_${n}`;
  };

  const save = async () => {
    if (form._scope === 'company' && !form.company_id) {
      toast.error(t('pages.roleProfiles.companyRequired'));
      return;
    }
    const autoMode = !editing && !form._codeManual;
    const finalCode = editing
      ? form.code
      : (autoMode ? computeAutoCode(form.position_title) : (form.code || '').trim());
    if (!editing && form._codeManual && items.some((p) => p.code === finalCode)) {
      toast.error(t('pages.roleProfiles.codeDuplicate'));
      return;
    }
    try {
      const { _scope, _codeManual, ...rest } = form;
      const payload = {
        ...rest,
        code: finalCode || slug(form.position_title),
        company_id: _scope === 'company' ? form.company_id : null,
      };
      if (editing) await adminApi.updateRoleProfile(editing.id, { ...payload, expected_updated_at: editing.updated_at });
      else await adminApi.createRoleProfile(payload, autoMode);
      toast.success(editing ? 'Profile updated' : 'Profile created');
      setOpen(false); reload();
    } catch (e) {
      // Phase 2C-1 — stale-save conflict: never silently overwrite newer changes.
      if (e?.response?.status === 409 && e?.response?.data?.detail?.code === 'ROLE_PROFILE_STALE') {
        toast.error(isEsUi
          ? 'Este perfil fue modificado por otra persona después de que usted lo abrió.'
          : 'This role profile was modified by someone else after you opened it.', {
          description: isEsUi
            ? 'Se recargó la versión más reciente. Revise los cambios y vuelva a intentar.'
            : 'The latest version was reloaded. Review the changes and try again.',
        });
        setOpen(false); reload();
        return;
      }
      toast.error(t('toasts.saveFailed'), { description: formatApiError(e) });
    }
  };

  const fork = async () => {
    if (!forkCompanyId.trim()) { toast.error(t('toasts.companyIdRequiredFork')); return; }
    try {
      await adminApi.forkRoleProfile(forkSrc.id, { company_id: forkCompanyId.trim() });
      toast.success(t('toasts.forkCreated')); setForkOpen(false); setForkCompanyId(''); reload();
    } catch (e) { toast.error(t('toasts.forkFailed'), { description: formatApiError(e) }); }
  };

  // Phase 3 — duplicate within the SAME tenant (rename allowed in the flow).
  const openDuplicate = (p) => {
    setDupSrc(p);
    setDupName(`${p.position_title} ${t('phase3.rp.copySuffix', { defaultValue: '(copy)' })}`);
    setDupOpen(true);
  };
  const duplicate = async () => {
    try {
      await adminApi.duplicateRoleProfile(dupSrc.id, { position_title: dupName.trim() || null });
      toast.success(t('phase3.rp.duplicated', { defaultValue: 'Profile duplicated within the same client' }));
      setDupOpen(false); reload();
    } catch (e) { toast.error(t('toasts.saveFailed'), { description: formatApiError(e) }); }
  };

  // Phase 3 — governed review before promoting to a Global CTH Template.
  const openPromote = (p) => {
    setPromoteSrc(p);
    setPromoteForm({
      position_title: p.position_title || '',
      department: p.department || '',
      description: p.description || '',
      role_context_notes: p.role_context_notes || '',
      position_purpose: p.position_purpose || '',
      main_responsibilities: p.main_responsibilities || '',
      minimum_requirements: p.minimum_requirements || '',
      academic_background: p.academic_background || '',
      required_experience: p.required_experience || '',
      requirements: (p.requirements || []).map((r) => ({ ...r })),
      industry: p.industry || 'general',
    });
    setPromoteOpen(true);
  };
  const promote = async () => {
    if (!promoteForm.position_title.trim()) {
      toast.error(t('phase3.rp.titleRequired', { defaultValue: 'Title is required' }));
      return;
    }
    setPromoteSaving(true);
    try {
      await adminApi.promoteRoleProfile(promoteSrc.id, {
        ...promoteForm,
        position_title: promoteForm.position_title.trim(),
      });
      toast.success(t('phase3.rp.promoted', { defaultValue: 'New Global CTH Template created (original profile untouched)' }));
      setPromoteOpen(false); reload();
    } catch (e) { toast.error(t('toasts.saveFailed'), { description: formatApiError(e) }); }
    finally { setPromoteSaving(false); }
  };
  const updatePromoteReq = (i, patch) => {
    setPromoteForm((f) => {
      const next = [...f.requirements];
      next[i] = { ...next[i], ...patch };
      return { ...f, requirements: next };
    });
  };

  const updateReq = (i, patch) => {
    const next = [...form.requirements];
    next[i] = { ...next[i], ...patch };
    setForm({ ...form, requirements: next });
  };

  return (
    <>
      <Topbar title={t('pages.roleProfiles.pageTitle')} subtitle={t('pages.roleProfiles.pageSubtitle')} />
      <div className="p-6 lg:p-8 space-y-6" data-testid="admin-roles-page">
        {/* UAT A3 — pending Job Profile requests visible in the Job Profile workspace */}
        <PendingJobProfileRequests />
        <ModuleBanner id="roleProfiles" icon={Briefcase} />
        <div className="flex items-center justify-between gap-3 flex-wrap">
          {/* Industry filter chip row (Prompt 17) */}
          <div className="flex items-center gap-2 flex-wrap" data-testid="rp-industry-filter">
            <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t('pages.roleProfiles.industry')}</Label>
            <Select value={industryFilter} onValueChange={setIndustryFilter}>
              <SelectTrigger className="h-9 w-44 text-xs" data-testid="rp-industry-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('pages.roleProfiles.allIndustries')}</SelectItem>
                {industries.map(i => <SelectItem key={i.code} value={i.code}>{industryLabel(i.code)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3">
            {/* FIX 3 — catalog text search */}
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="rp-search-input"
                className="h-10 w-64 pl-9"
                placeholder={t('pages.roleProfiles.searchPlaceholder', { defaultValue: 'Buscar por posición, código, departamento…' })}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2 px-3 h-10 rounded-md border border-border">
              <Switch checked={showArchived} onCheckedChange={setShowArchived} />
              <Label className="text-xs">{t('pages.roleProfiles.showArchived')}</Label>
            </div>
            {isHmAdmin && (
              <Button variant="outline" data-testid="rp-import-btn" onClick={() => setImportOpen(true)} className="h-10 gap-1.5">
                <FileSpreadsheet className="h-4 w-4" /> {isEsUi ? 'Importar Excel' : 'Import Excel'}
              </Button>
            )}
            <Button data-testid="rp-new-btn" onClick={openNew} className="bg-gradient-primary h-10 gap-1.5"><Plus className="h-4 w-4" /> {t('pages.roleProfiles.newProfile')}</Button>
          </div>
        </div>

        <Card className="shadow-card overflow-hidden">
          {loading ? <div className="p-12 flex items-center justify-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('pages.roleProfiles.loading')}</div>
          : items.length === 0 ? <div className="p-12 text-center"><Briefcase className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" /><p className="text-sm text-muted-foreground">{t('pages.roleProfiles.noneYet')}</p></div>
          : <Table>
              <TableHeader><TableRow><TableHead><SortableHeader sort={sort} k="position">{t('pages.roleProfiles.position')}</SortableHeader></TableHead><TableHead><SortableHeader sort={sort} k="code">{t('pages.roleProfiles.code')}</SortableHeader></TableHead><TableHead><SortableHeader sort={sort} k="scope">{t('pages.roleProfiles.scope')}</SortableHeader></TableHead><TableHead><SortableHeader sort={sort} k="requirements">{t('pages.roleProfiles.requirements')}</SortableHeader></TableHead><TableHead><SortableHeader sort={sort} k="status">{t('pages.roleProfiles.status')}</SortableHeader></TableHead><TableHead className="text-right">{t('pages.roleProfiles.actions')}</TableHead></TableRow></TableHeader>
              <TableBody>
                {sortedItems.map((p) => (
                  <TableRow key={p.id} data-testid={`rp-row-${p.code}`}>
                    <TableCell>
                      <button
                        type="button"
                        data-testid={`rp-open-detail-${p.code}`}
                        onClick={() => setViewP(p)}
                        className="text-left font-medium hover:text-primary hover:underline underline-offset-2 cursor-pointer"
                      >
                        {tData(p.position_title)}
                      </button>
                      <p className="text-xs text-muted-foreground">{tData(p.department) || '—'}{p.parent_profile_id ? ` · ${t('pages.roleProfiles.forked')}` : ''}</p>
                    </TableCell>
                    <TableCell className="font-mono text-xs" title={p.code}>{p.parent_profile_id ? String(p.code || '').replace(/-fork-\d+$/, '') : p.code}</TableCell>
                    <TableCell data-testid={`rp-origin-${p.code}`}>
                      {p.company_id ? (
                        <Badge variant="outline" className="text-xs gap-1"><Building2 className="h-3 w-3" />{companies.find((c) => c.id === p.company_id)?.name || t('pages.roleProfiles.company')}</Badge>
                      ) : (
                        <Badge className="bg-primary/10 text-primary border-primary/20 gap-1" variant="outline"><Globe className="h-3 w-3" />{t('pages.roleProfiles.originGlobal')}</Badge>
                      )}
                      {p.industry ? <Badge variant="outline" className="text-xs ml-1.5">{industryLabel(p.industry)}</Badge> : null}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{(p.requirements || []).length} {t('pages.roleProfiles.competencies')}</TableCell>
                    <TableCell>{p.archived ? <Badge variant="outline" className="text-muted-foreground">{t('pages.roleProfiles.archived')}</Badge> : <Badge className="bg-success/10 text-success border-success/20" variant="outline">{t('pages.roleProfiles.active')}</Badge>}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" data-testid={`rp-view-${p.code}`} onClick={() => setViewP(p)} className="gap-1"><Eye className="h-3.5 w-3.5" /> {t('phase3.rp.view', { defaultValue: 'Ver' })}</Button>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(p)} className="gap-1"><Pencil className="h-3.5 w-3.5" /> {t('pages.roleProfiles.edit')}</Button>
                      {!p.company_id && <Button size="sm" variant="ghost" data-testid={`rp-fork-${p.code}`} onClick={() => { setForkSrc(p); setForkOpen(true); }} className="gap-1"><Copy className="h-3.5 w-3.5" /> {t('pages.roleProfiles.fork')}</Button>}
                      {p.company_id && (
                        <Button size="sm" variant="ghost" data-testid={`rp-duplicate-${p.code}`} onClick={() => openDuplicate(p)} className="gap-1">
                          <Files className="h-3.5 w-3.5" /> {t('phase3.rp.duplicate', { defaultValue: 'Duplicate' })}
                        </Button>
                      )}
                      {p.company_id && isHmAdmin && (
                        <Button size="sm" variant="ghost" data-testid={`rp-promote-${p.code}`} onClick={() => openPromote(p)} className="gap-1 text-primary">
                          <ArrowUpRight className="h-3.5 w-3.5" /> {t('phase3.rp.promote', { defaultValue: 'Promote to global' })}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>}
        </Card>
      </div>

      {/* Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle>
            {editing
              ? (form._scope === 'company'
                ? t('pages.roleProfiles.editCompanyTitle', { company: companies.find((c) => c.id === form.company_id)?.name || t('pages.roleProfiles.company') })
                : t('pages.roleProfiles.editGlobalTitle'))
              : t('pages.roleProfiles.newTitle')}
          </DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-3">
                <div><Label>{t('pages.roleProfiles.positionTitle')}</Label><Input data-testid="rp-form-title" className="mt-1.5" value={form.position_title} onChange={(e) => setForm({ ...form, position_title: e.target.value })} /></div>
                <div><Label>{t('pages.roleProfiles.department')}</Label><Input className="mt-1.5" value={form.department || ''} onChange={(e) => setForm({ ...form, department: e.target.value })} /></div>
                {!editing && (
                  <div>
                    <Label>{t('pages.roleProfiles.code')}</Label>
                    {form._codeManual ? (
                      <>
                        <Input data-testid="rp-form-code" className="mt-1.5" placeholder={slug(form.position_title) + '_v1'} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
                        <button type="button" className="text-xs text-primary hover:underline mt-1" data-testid="rp-code-use-auto" onClick={() => setForm({ ...form, _codeManual: false, code: '' })}>{t('pages.roleProfiles.useAutoCode')}</button>
                      </>
                    ) : (
                      <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                        <code data-testid="rp-code-preview" className="text-xs bg-muted/60 px-2 py-1 rounded">{computeAutoCode(form.position_title) || '—'}</code>
                        <span className="text-xs text-muted-foreground">{t('pages.roleProfiles.codeAutoHint')}</span>
                        <button type="button" className="text-xs text-primary hover:underline" data-testid="rp-code-edit" onClick={() => setForm({ ...form, _codeManual: true, code: computeAutoCode(form.position_title) })}>{t('pages.roleProfiles.editCode')}</button>
                      </div>
                    )}
                  </div>
                )}
                <div>
                  <Label>{t('pages.roleProfiles.scopeLabel')}</Label>
                  <Select value={form._scope} onValueChange={(v) => setForm({ ...form, _scope: v, company_id: v === 'global' ? null : form.company_id })}>
                    <SelectTrigger data-testid="rp-form-scope" className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="global">{t('pages.roleProfiles.scopeGlobal')}</SelectItem>
                      <SelectItem value="company">{t('pages.roleProfiles.scopeCompany')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form._scope === 'company' && (
                  <div>
                    <Label>{t('pages.roleProfiles.companyLabel')}</Label>
                    <Select value={form.company_id || ''} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                      <SelectTrigger data-testid="rp-form-company" className="mt-1.5"><SelectValue placeholder={t('pages.roleProfiles.companyPh')} /></SelectTrigger>
                      <SelectContent>
                        {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div><Label>{t('pages.roleProfiles.description')}</Label><Textarea className="mt-1.5" value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div>
                <Label>{t('pages.roleProfiles.roleNotes')}</Label>
                <p className="text-xs text-muted-foreground mt-0.5">{t('pages.roleProfiles.roleNotesHint')}</p>
                <Textarea className="mt-1.5 min-h-[88px]" value={form.role_context_notes || ''} onChange={(e) => setForm({ ...form, role_context_notes: e.target.value })} />
              </div>

              <div data-testid="rp-job-definition">
                <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t('pages.roleProfiles.jobDefinition')}</Label>
                <p className="text-xs text-muted-foreground mt-0.5 mb-2">{t('pages.roleProfiles.jobDefinitionHint')}</p>
                <div className="space-y-3">
                  {JOB_DEF_FIELDS.map(([field, key]) => (
                    <div key={field}>
                      <Label className="text-xs">{t(`pages.roleProfiles.${key}`)}</Label>
                      <Textarea data-testid={`rp-form-${field}`} className="mt-1 min-h-[60px] text-sm" value={form[field] || ''} onChange={(e) => setForm({ ...form, [field]: e.target.value })} />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t('pages.roleProfiles.requiredComps')}</Label>
                  <Button size="sm" variant="ghost" onClick={() => setForm({ ...form, requirements: [...form.requirements, EMPTY_REQ()] })}><Plus className="h-3.5 w-3.5 mr-1" /> {t('pages.roleProfiles.add')}</Button>
                </div>
                <div className="space-y-2">
                  <div className="grid grid-cols-12 gap-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold px-2" data-testid="rp-matrix-headers">
                    <span className="col-span-4">{t('pages.roleProfiles.competency')}</span>
                    <span className="col-span-2 flex items-center gap-1" title={t('pages.roleProfiles.weightTip')}>{t('pages.roleProfiles.weightFull')} <Info className="h-3 w-3" /></span>
                    <span className="col-span-2 flex items-center gap-1" title={t('pages.roleProfiles.expectedTip')}>{t('pages.roleProfiles.expectedFull')} <Info className="h-3 w-3" /></span>
                    <span className="col-span-2 flex items-center gap-1" title={t('pages.roleProfiles.minTip')}>{t('pages.roleProfiles.minFull')} <Info className="h-3 w-3" /></span>
                    <span className="col-span-1 flex items-center gap-1" title={t('pages.roleProfiles.critTip')}>{t('pages.roleProfiles.critFull')} <Info className="h-3 w-3" /></span>
                    <span className="col-span-1" />
                  </div>
                  {form.requirements.map((r, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center border border-border rounded-lg p-2">
                      <div className="col-span-4">
                        <Select value={r.competency_code} onValueChange={(v) => updateReq(i, { competency_code: v })}>
                          <SelectTrigger data-testid={`req-${i}-comp`} className="h-9 text-xs"><SelectValue placeholder={t('pages.roleProfiles.pickCompetency')} /></SelectTrigger>
                          <SelectContent>{comps.filter((c) => !c.archived).map((c) => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <Input className="col-span-2 h-9 text-xs" type="number" step={0.1} value={r.weight} onChange={(e) => updateReq(i, { weight: parseFloat(e.target.value) || 0 })} title={t('pages.roleProfiles.weightTip')} />
                      <Input className="col-span-2 h-9 text-xs" type="number" min={0} max={100} value={r.expected_level} onChange={(e) => updateReq(i, { expected_level: parseInt(e.target.value || '0', 10) })} title={t('pages.roleProfiles.expectedTip')} />
                      <Input className="col-span-2 h-9 text-xs" type="number" min={0} max={100} value={r.minimum_threshold} onChange={(e) => updateReq(i, { minimum_threshold: parseInt(e.target.value || '0', 10) })} title={t('pages.roleProfiles.minTip')} />
                      <Select value={r.criticality} onValueChange={(v) => updateReq(i, { criticality: v })}>
                        <SelectTrigger className="col-span-1 h-9 text-xs" title={t('pages.roleProfiles.critTip')}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="critical">{t('pages.roleProfiles.critCritical')}</SelectItem>
                          <SelectItem value="important">{t('pages.roleProfiles.critImportant')}</SelectItem>
                          <SelectItem value="nice_to_have">{t('pages.roleProfiles.critNice')}</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button size="sm" variant="ghost" className="col-span-1 h-9 w-9 p-0 text-muted-foreground hover:text-destructive" onClick={() => setForm({ ...form, requirements: form.requirements.filter((_, j) => j !== i) })}><Minus className="h-3.5 w-3.5" /></Button>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2 flex items-start gap-1.5" data-testid="rp-matrix-help">
                  <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  {t('pages.roleProfiles.matrixHelp')}
                </p>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="ghost" onClick={() => setOpen(false)}>{t('pages.roleProfiles.cancel')}</Button><Button data-testid="rp-form-save" onClick={save} className="bg-gradient-primary">{editing ? t('pages.roleProfiles.save') : t('pages.roleProfiles.create')}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fork dialog */}
      <Dialog open={industryPickerOpen} onOpenChange={setIndustryPickerOpen}>
        <DialogContent className="max-w-lg" data-testid="rp-industry-picker-dialog">
          <DialogHeader>
            <DialogTitle>{t('pages.roleProfiles.pickIndustry')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">{t('pages.roleProfiles.pickIndustryHint')}</p>
          <div className="grid grid-cols-3 gap-2 py-3">
            {industries.map(i => (
              <button
                key={i.code}
                onClick={() => setDraftIndustry(i.code)}
                data-testid={`rp-industry-opt-${i.code}`}
                className={`p-3 rounded-md border text-sm font-medium transition ${draftIndustry === i.code ? 'border-primary bg-primary/5 text-primary' : 'border-border hover:bg-muted/40'}`}
              >
                {industryLabel(i.code)}
              </button>
            ))}
          </div>
          {/* Pre-loaded role suggestions for the chosen industry */}
          <div className="border-t pt-3 space-y-2 max-h-56 overflow-y-auto" data-testid="rp-industry-suggestions">
            <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t('pages.roleProfiles.standardRoles')}</Label>
            <div className="grid grid-cols-2 gap-2">
              {items.filter(p => p.industry === draftIndustry && !p.company_id).slice(0, 8).map(p => (
                <button
                  key={p.id}
                  onClick={() => { setIndustryPickerOpen(false); openEdit(p); }}
                  className="text-left p-2 rounded border border-border hover:border-primary hover:bg-primary/5 text-xs"
                  data-testid={`rp-suggestion-${p.code}`}
                >
                  <div className="font-semibold">{tData(p.position_title)}</div>
                  <div className="text-muted-foreground">{tData(p.department) || '—'} · {(p.requirements || []).length} comp.</div>
                </button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIndustryPickerOpen(false)} data-testid="rp-industry-cancel">{t('pages.roleProfiles.cancel')}</Button>
            <Button onClick={() => proceedToNewRole(draftIndustry)} data-testid="rp-industry-blank" className="bg-gradient-primary">{t('pages.roleProfiles.startBlank', { industry: industryLabel(draftIndustry) || t('pages.roleProfiles.industryFallback') })}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fork dialog */}
      <Dialog open={forkOpen} onOpenChange={setForkOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('pages.roleProfiles.forkTitle', { title: tData(forkSrc?.position_title) })}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">{t('pages.roleProfiles.forkSubtitle')}</p>
          <div className="py-2">
            <Label>{t('pages.roleProfiles.targetCompanyId')}</Label>
            <Select value={forkCompanyId} onValueChange={setForkCompanyId}>
              <SelectTrigger data-testid="fork-company-id" className="mt-1.5"><SelectValue placeholder={t('pages.roleProfiles.targetCompanyHint')} /></SelectTrigger>
              <SelectContent>
                {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setForkOpen(false)}>{t('pages.roleProfiles.cancel')}</Button><Button data-testid="fork-confirm" onClick={fork} className="bg-gradient-primary">{t('pages.roleProfiles.createFork')}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 3 — Duplicate (same tenant) */}
      <Dialog open={dupOpen} onOpenChange={setDupOpen}>
        <DialogContent data-testid="rp-duplicate-dialog">
          <DialogHeader>
            <DialogTitle>{t('phase3.rp.duplicateTitle', { defaultValue: 'Duplicate role profile' })}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">
            {t('phase3.rp.duplicateHint', { defaultValue: 'Creates an independent copy within the SAME client. The original profile is never modified. Cross-tenant reuse must go through the global template promotion.' })}
          </p>
          <div className="py-2">
            <Label>{t('phase3.rp.newName', { defaultValue: 'Name of the new profile' })}</Label>
            <Input className="mt-1.5" value={dupName} onChange={(e) => setDupName(e.target.value)} data-testid="rp-duplicate-name" />
            <p className="text-xs text-muted-foreground mt-1.5">
              <Building2 className="h-3 w-3 inline mr-1" />
              {companies.find((c) => c.id === dupSrc?.company_id)?.name || t('pages.roleProfiles.company')}
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDupOpen(false)}>{t('pages.roleProfiles.cancel')}</Button>
            <Button data-testid="rp-duplicate-confirm" onClick={duplicate} className="bg-gradient-primary">
              {t('phase3.rp.duplicateConfirm', { defaultValue: 'Create duplicate' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 3 — Promote to Global CTH Template (admin only, governed review) */}
      <Dialog open={promoteOpen} onOpenChange={setPromoteOpen}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="rp-promote-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-primary" />
              {t('phase3.rp.promoteTitle', { defaultValue: 'Promote to Global CTH Template' })}
            </DialogTitle>
          </DialogHeader>
          {promoteForm && (
            <div className="space-y-4">
              <div className="rounded-md border border-warning/40 bg-warning/10 p-3 text-sm" data-testid="rp-promote-warning">
                <p className="font-semibold text-warning">{t('phase3.rp.promoteWarningTitle', { defaultValue: 'Review before creating' })}</p>
                <p className="text-muted-foreground text-xs mt-1">
                  {t('phase3.rp.promoteWarning', { defaultValue: 'A NEW independent global template will be created; the original client profile stays unchanged and private. Tenant references are stripped automatically. Review every text field below and remove any language that could identify the source client.' })}
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label>{t('pages.roleProfiles.positionTitle')}</Label>
                  <Input className="mt-1.5" data-testid="rp-promote-title" value={promoteForm.position_title}
                         onChange={(e) => setPromoteForm({ ...promoteForm, position_title: e.target.value })} />
                </div>
                <div>
                  <Label>{t('pages.roleProfiles.department')}</Label>
                  <Input className="mt-1.5" value={promoteForm.department}
                         onChange={(e) => setPromoteForm({ ...promoteForm, department: e.target.value })} />
                </div>
                <div>
                  <Label>{t('pages.roleProfiles.industry')}</Label>
                  <Select value={promoteForm.industry} onValueChange={(v) => setPromoteForm({ ...promoteForm, industry: v })}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {industries.map((i) => <SelectItem key={i.code} value={i.code}>{industryLabel(i.code)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>{t('pages.roleProfiles.description')}</Label>
                <Textarea className="mt-1.5" value={promoteForm.description}
                          onChange={(e) => setPromoteForm({ ...promoteForm, description: e.target.value })} />
              </div>
              <div>
                <Label>{t('pages.roleProfiles.roleNotes')}</Label>
                <Textarea className="mt-1.5" value={promoteForm.role_context_notes}
                          onChange={(e) => setPromoteForm({ ...promoteForm, role_context_notes: e.target.value })} />
              </div>
              <div className="space-y-3">
                {JOB_DEF_FIELDS.map(([field, key]) => (
                  <div key={field}>
                    <Label className="text-xs">{t(`pages.roleProfiles.${key}`)}</Label>
                    <Textarea className="mt-1 min-h-[54px] text-sm" value={promoteForm[field] || ''}
                              onChange={(e) => setPromoteForm({ ...promoteForm, [field]: e.target.value })} />
                  </div>
                ))}
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                  {t('pages.roleProfiles.requiredComps')}
                </Label>
                <div className="space-y-2 mt-2">
                  {promoteForm.requirements.map((r, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center border border-border rounded-lg p-2">
                      <span className="col-span-4 text-sm">{compByCode[r.competency_code]?.name || r.competency_code}</span>
                      <Input className="col-span-2 h-9 text-xs" type="number" step={0.1} value={r.weight}
                             onChange={(e) => updatePromoteReq(i, { weight: parseFloat(e.target.value) || 0 })} title={t('pages.roleProfiles.weightTip')} />
                      <Input className="col-span-2 h-9 text-xs" type="number" min={0} max={100} value={r.expected_level}
                             onChange={(e) => updatePromoteReq(i, { expected_level: parseInt(e.target.value || '0', 10) })} title={t('pages.roleProfiles.expectedTip')} />
                      <Input className="col-span-2 h-9 text-xs" type="number" min={0} max={100} value={r.minimum_threshold}
                             onChange={(e) => updatePromoteReq(i, { minimum_threshold: parseInt(e.target.value || '0', 10) })} title={t('pages.roleProfiles.minTip')} />
                      <span className="col-span-1 text-xs text-muted-foreground">{t(`pages.roleProfiles.criticality.${r.criticality}`, { defaultValue: r.criticality })}</span>
                      <Button size="sm" variant="ghost" className="col-span-1 h-9 w-9 p-0 text-muted-foreground hover:text-destructive"
                              onClick={() => setPromoteForm({ ...promoteForm, requirements: promoteForm.requirements.filter((_, j) => j !== i) })}>
                        <Minus className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                  {!promoteForm.requirements.length && (
                    <p className="text-xs text-muted-foreground italic">{t('phase3.rp.noReqs', { defaultValue: 'No competency requirements.' })}</p>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPromoteOpen(false)}>{t('pages.roleProfiles.cancel')}</Button>
            <Button data-testid="rp-promote-confirm" onClick={promote} disabled={promoteSaving} className="bg-gradient-primary">
              {promoteSaving
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : t('phase3.rp.promoteConfirm', { defaultValue: 'Create global template' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Catalog cleanup sprint J — read-only profile detail (no edit mode) */}
      <Dialog open={!!viewP} onOpenChange={(o) => !o && setViewP(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="rp-detail-dialog">
          {viewP && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Briefcase className="h-5 w-5 text-primary" /> {tData(viewP.position_title)}
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <div><p className="text-xs text-muted-foreground">{t('pages.roleProfiles.code')}</p><p className="font-mono text-xs">{viewP.code}</p></div>
                <div><p className="text-xs text-muted-foreground">{t('pages.roleProfiles.status')}</p><p>{viewP.archived ? t('pages.roleProfiles.archived') : t('pages.roleProfiles.active')}</p></div>
                <div><p className="text-xs text-muted-foreground">{t('pages.roleProfiles.scope')}</p><p>{viewP.company_id ? (companies.find((c) => c.id === viewP.company_id)?.name || t('pages.roleProfiles.company')) : t('pages.roleProfiles.originGlobal')}</p></div>
                <div><p className="text-xs text-muted-foreground">{t('phase3.rp.industry', { defaultValue: 'Industria' })}</p><p>{viewP.industry ? industryLabel(viewP.industry) : '—'}</p></div>
                <div><p className="text-xs text-muted-foreground">{t('phase3.rp.department', { defaultValue: 'Departamento' })}</p><p>{tData(viewP.department) || '—'}</p></div>
                <div><p className="text-xs text-muted-foreground">{t('phase3.rp.updated', { defaultValue: 'Última modificación' })}</p><p>{String(viewP.updated_at || viewP.created_at || '').slice(0, 10) || '—'}</p></div>
              </div>
              {JOB_DEF_FIELDS.map(([field]) => viewP[field] ? (
                <div key={field} className="mt-2">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t(`pages.roleProfiles.${field}`, { defaultValue: field.replace(/_/g, ' ') })}</p>
                  <p className="text-sm whitespace-pre-wrap mt-0.5">{tData(viewP[field])}</p>
                </div>
              ) : null)}
              <div className="mt-2">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {t('pages.roleProfiles.requirements')} ({(viewP.requirements || []).length})
                </p>
                <div className="mt-1.5 space-y-1.5" data-testid="rp-detail-requirements">
                  {(viewP.requirements || []).map((r, idx) => {
                    const comp = comps.find((c) => c.code === r.competency_code);
                    return (
                      <div key={idx} className="flex items-center justify-between rounded border border-border px-3 py-1.5 text-sm">
                        <span>{comp ? tData(comp.name) : r.competency_code}</span>
                        <span className="text-xs text-muted-foreground">
                          {t('pages.roleProfiles.expected', { defaultValue: 'Esperado' })}: {r.expected_level} · {t('pages.roleProfiles.minimum', { defaultValue: 'Mínimo' })}: {r.minimum_threshold} · {t(`pages.roleProfiles.criticality.${r.criticality}`, { defaultValue: r.criticality })}
                        </span>
                      </div>
                    );
                  })}
                  {(viewP.requirements || []).length === 0 && <p className="text-xs text-muted-foreground italic">—</p>}
                </div>
              </div>
              <DialogFooter className="flex-wrap gap-2">
                {user?.role === 'hm_admin' && !viewP.archived && (
                  <Button variant="outline" className="gap-1 text-amber-700 border-amber-300" data-testid="rp-detail-archive"
                          onClick={async () => {
                            try {
                              await api.post(`/admin/archive/role_profile/${viewP.id}`, { reason_code: 'manual_archive' });
                              toast.success(t('toasts.archived', { defaultValue: 'Archived' }), { description: tData(viewP.position_title) });
                              setViewP(null); reload();
                            } catch (e) { toast.error(formatApiError(e)); }
                          }}>
                    <Archive className="h-3.5 w-3.5" /> {t('pages.roleProfiles.archive', { defaultValue: 'Archivar' })}
                  </Button>
                )}
                {user?.role === 'hm_admin' && viewP.archived && (
                  <Button variant="outline" className="gap-1 text-emerald-700 border-emerald-300" data-testid="rp-detail-restore"
                          onClick={async () => {
                            try {
                              await api.post(`/admin/trash/role_profile/${viewP.id}/restore`);
                              toast.success(t('trash.restoredToast', { defaultValue: 'Restored' }));
                              setViewP(null); reload();
                            } catch (e) { toast.error(formatApiError(e)); }
                          }}>
                    <RotateCcw className="h-3.5 w-3.5" /> {t('trash.restore', { defaultValue: 'Restaurar' })}
                  </Button>
                )}
                {user?.role === 'hm_admin' && (
                  <Button variant={rpDelArmed ? 'destructive' : 'ghost'} className="gap-1" data-testid="rp-detail-delete"
                          onClick={async () => {
                            try {
                              if (!rpDelArmed) {
                                const { data: dep } = await api.get(`/admin/trash/role_profile/${viewP.id}/dependencies`);
                                if (!dep.safe) {
                                  const parts = (dep.blockers || []).map((b) => `${b.count} ${b.kind.replace(/_/g, ' ')}`).join(', ');
                                  toast.error(t('trash.deleteBlocked', { defaultValue: 'Cannot delete permanently' }), {
                                    description: t('trash.deleteBlockedDesc', { defaultValue: 'This record has {{deps}}. It stays archived.', deps: parts }),
                                  });
                                  return;
                                }
                                setRpDelArmed(true);
                                return;
                              }
                              await api.delete(`/admin/trash/role_profile/${viewP.id}`);
                              toast.success(t('trash.deletedToast', { defaultValue: 'Permanently deleted (backup + audit).' }));
                              setRpDelArmed(false); setViewP(null); reload();
                            } catch (e) { toast.error(formatApiError(e)); }
                          }}>
                    <Trash2 className="h-3.5 w-3.5" />
                    {rpDelArmed ? t('trash.confirmDeleteBtn', { defaultValue: 'Eliminar permanentemente' }) : t('trash.deletePermanently', { defaultValue: 'Eliminar' })}
                  </Button>
                )}
                <Button variant="outline" onClick={() => { setRpDelArmed(false); setViewP(null); }} data-testid="rp-detail-close">{t('common.close', { defaultValue: 'Cerrar' })}</Button>
                <Button onClick={() => { const p = viewP; setViewP(null); openEdit(p); }} data-testid="rp-detail-edit" className="gap-1"><Pencil className="h-3.5 w-3.5" /> {t('pages.roleProfiles.edit')}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      <RoleProfileImportDialog open={importOpen} onOpenChange={setImportOpen} onImported={reload} />
    </>
  );
}
